import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { PasswordResetModel } from '../../../models/user.model';
import { AuthService } from '../../../services/auth.service';
import { translateErrorMessage } from '../../../utils/language.utils';
import { passwordRequirementsValidator } from '../../../validators/password-requirements.validator';

@Component({
  selector: 'app-password-reset',
  templateUrl: './password-reset.component.html',
  styleUrl: './password-reset.component.scss',
  standalone: false,
})
export class PasswordResetComponent implements OnInit, OnDestroy {
  passwordMismatch = false;
  public resetPasswordForm: FormGroup;
  private resetToken: string;
  errorMessage = '';
  successMessage = '';
  public showPassword = false;
  public showPasswordCheck = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService
  ) {
    this.createForm();
  }

  ngOnInit() {
    // Get token from the URL query parameters
    this.route.queryParams.subscribe(params => {
      this.resetToken = params['token'];
    });
  }

  private destroy$ = new Subject<void>();

  public fieldRequired(formField: AbstractControl): boolean {
    return formField.hasValidator(Validators.required);
  }

  // Variables
  private createForm() {
    this.resetPasswordForm = new FormGroup({
      password: new FormControl('', [
        Validators.required,
        passwordRequirementsValidator(),
      ]),
      passwordCheck: new FormControl('', [
        Validators.required,
        passwordRequirementsValidator(),
      ]),
    });
    this.verifyPasswordsMatch();
  }

  // subscribe to the valueChanges of the password and passwordCheck fields
  private verifyPasswordsMatch() {
    this.resetPasswordForm
      .get('passwordCheck')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => {
        const password = this.resetPasswordForm.get('password').value;
        if (password !== value) {
          this.passwordMismatch = true;
          this.resetPasswordForm
            .get('passwordCheck')
            .setErrors({ mismatch: true });
        } else {
          this.passwordMismatch = false;
        }
      });
    this.resetPasswordForm
      .get('password')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => {
        if (!this.resetPasswordForm.get('passwordCheck').touched) {
          return;
        }
        const passwordCheck = this.resetPasswordForm.get('passwordCheck').value;
        if (passwordCheck !== value) {
          this.passwordMismatch = true;
          this.resetPasswordForm
            .get('passwordCheck')
            .setErrors({ mismatch: true });
        } else {
          this.passwordMismatch = false;
        }
      });
  }

  public resetPassword() {
    if (!this.resetPasswordForm.valid) {
      this.resetPasswordForm.markAllAsTouched();
      console.error('Form not valid');
      return;
    }
    this.errorMessage = '';
    this.successMessage = '';
    const passwordResetUser: PasswordResetModel = {
      token: this.resetToken,
      password: this.resetPasswordForm.value.password,
    };

    this.authService
      .resetPassword(passwordResetUser)
      .then(() => {
        this.successMessage =
          'Super, dein Passwort ist jetzt geändert! Melde dich einfach an.';
        this.router.navigate(['/login'], {
          queryParams: { reset: 'true' },
        });
      })
      .catch(error => {
        this.errorMessage = translateErrorMessage(error.message);
      });
  }

  public togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  public togglePasswordCheckVisibility() {
    this.showPasswordCheck = !this.showPasswordCheck;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
